import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import auth from '../../api/auth';
import fetchPatientAddress, { Add } from '../../api/fetchPatientAddress';
import setPatientAddress from '../../api/setPatientAddress';
import AppHeader from '../../components/AppHeader';
import { track } from '../../tracking/mixpanel';
import { DELAY_DUE_TO_RIPPLE } from '../vitals/VitalsHome';

type RouteProps = {
    patient_oid: string;
    magiclink_hash?: string;
};

const initialFormInput = {
    _id: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    place_id: '',
    coordinates: '',
    pincode: '',
};

const AddAddress: React.FC = () => {
    const [formInput, setFormInput] = useState(initialFormInput);
    const [patientAddressArray, setPatientAddressArray] = useState<Add[]>([]);
    const [isNewOrEditAddress, setIsNewOrEditAddress] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const history = useHistory();
    const { patient_oid, magiclink_hash }: RouteProps = useParams();

    const goBack = () => {
        setTimeout(() => {
            history.goBack();
        }, DELAY_DUE_TO_RIPPLE);
    };

    useEffect(() => {
        async function initAutoSuggest() {
            const input = document.getElementById('line2') as HTMLInputElement;

            if (!input) {
                return;
            }

            const options = {
                strictBounds: false,
                componentRestrictions: { country: 'in' },
                fields: [
                    'name',
                    'geometry.location',
                    'place_id',
                    'formatted_address',
                    'address_components',
                ],
            };
            const autocomplete = new window.google.maps.places.Autocomplete(input, options);
            window.google.maps.event.addListener(autocomplete, 'place_changed', function () {
                const place = autocomplete.getPlace();
                if (!place || !place.geometry || !place.geometry.location) {
                    return;
                }
                const lat = place.geometry?.location.lat();
                const lng = place.geometry.location.lng();
                const { name = '', place_id = '', address_components = [] } = place;
                const formatted_address: string[] = [name];
                const additionalInfo: {
                    pincode?: string;
                    city?: string;
                    state?: string;
                } = {};
                address_components?.forEach((addComp) => {
                    if (addComp.types.includes('postal_code')) {
                        additionalInfo.pincode = addComp.long_name;
                    }
                    if (addComp.types.includes('locality')) {
                        additionalInfo.city = addComp.long_name;
                    }
                    if (addComp.types.includes('administrative_area_level_1')) {
                        additionalInfo.state = addComp.long_name;
                    }
                    if (addComp.types.includes('sublocality') || addComp.types.includes('route')) {
                        formatted_address?.push(addComp.long_name);
                    }
                });

                setFormInput({
                    ...formInput,
                    line2: formatted_address?.filter(Boolean).join(', ') || '',
                    city: additionalInfo.city || '',
                    state: additionalInfo.state || '',
                    place_id,
                    pincode: additionalInfo.pincode || '',
                    coordinates: `${lat},${lng}`,
                });
                const ele = document.getElementById('line');
                ele?.focus();
            });
        }
        if (window.google) {
            initAutoSuggest();
        }
    }, [isLoading, isNewOrEditAddress]);

    useEffect(() => {
        auth(magiclink_hash).finally(() =>
            fetchPatientAddress(patient_oid)
                .then((i) => {
                    setPatientAddressArray(i.address);
                    if (i.address?.length) setIsNewOrEditAddress(false);
                    if (i.success) setIsLoading(false);
                    else alert(i.error);
                })
                .catch((err) => {
                    alert(err);
                }),
        );
    }, []);

    const formInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value } = e.target;
        setFormInput({
            ...formInput,
            [id]: value,
        });
    };

    const setAddressHandler = () => {
        const { line1, line2, city, state, pincode } = formInput;
        if (!line1.trim() || !line2.trim() || !city.trim() || !state.trim() || !pincode.trim()) {
            alert('Please fill all fields');
            return;
        }
        setIsLoading(true);
        setPatientAddress({
            _id: formInput._id,
            pid: patient_oid || '',
            address: {
                ...formInput,
            },
        })
            .then((i) => {
                if (i.success) {
                    fetchPatientAddress(patient_oid)
                        .then((i) => {
                            setPatientAddressArray(i.address);
                        })
                        .catch((err) => {
                            console.log(err);
                        });
                    setFormInput(initialFormInput);
                    setIsNewOrEditAddress(false);
                    track('Add_Address_Success', { patient_oid });
                } else {
                    alert(i.error);
                    track('Add_Address_Error', { patient_oid, error: i.error });
                }
                setIsLoading(false);
                setIsEditing(false);
            })
            .catch((err) => {
                setIsLoading(false);
                alert(err);
            });
    };

    const editAddressHandler = (index: number) => {
        if (patientAddressArray?.[index]) {
            setIsEditing(true);
            const { address, _id } = patientAddressArray[index];
            setIsNewOrEditAddress(true);
            setFormInput({
                ...address,
                _id,
            });
        }
    };

    return (
        <div className="flex flex-col h-full font-grotesk">
            <AppHeader title="Add Address" goBack={goBack} />

            {isLoading ? (
                <div className="flex-1 space-y-16 p-14 overflow-auto hide-scrollbar mt-2 bg-background-secondary">
                    <div className="flex flex-col p-14 bg-white rounded-12 shadow-usecase-card-elevated">
                        <div className="shine w-150"></div>
                        <div className="shine"></div>
                        <div className="shine w-150"></div>
                    </div>

                    <div className="flex flex-col p-14 bg-white rounded-12 shadow-usecase-card-elevated">
                        <div className="shine w-150"></div>
                        <div className="shine"></div>
                        <div className="shine w-150"></div>
                    </div>

                    <div className="flex flex-col p-14 bg-white rounded-12 shadow-usecase-card-elevated">
                        <div className="shine w-150"></div>
                        <div className="shine"></div>
                        <div className="shine w-150"></div>
                    </div>
                </div>
            ) : isNewOrEditAddress ? (
                <div className="flex-1 px-16 py-14 overflow-auto space-y-16 add-address-form text-14 mt-2 bg-background-secondary">
                    <div className="space-y-7">
                        <p className="font-700">Address</p>

                        <div className="relative">
                            <input
                                id="line2"
                                className="border border-darwin-neutral-200 p-10 pl-38 rounded-8 w-full"
                                placeholder="Area, street name..."
                                autoFocus
                                value={formInput.line2}
                                onChange={formInputChangeHandler}
                                required
                            />
                            <svg
                                className="absolute top-14 left-13 w-16 h-16 fill-darwin-neutral-1000"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                            >
                                <path d="M504.1 471l-134-134C399.1 301.5 415.1 256.8 415.1 208c0-114.9-93.13-208-208-208S-.0002 93.13-.0002 208S93.12 416 207.1 416c48.79 0 93.55-16.91 129-45.04l134 134C475.7 509.7 481.9 512 488 512s12.28-2.344 16.97-7.031C514.3 495.6 514.3 480.4 504.1 471zM48 208c0-88.22 71.78-160 160-160s160 71.78 160 160s-71.78 160-160 160S48 296.2 48 208z" />
                            </svg>
                        </div>
                    </div>

                    <div className="space-y-7">
                        <p className="font-700">House / Flat Name and No.</p>
                        <input
                            id="line1"
                            className="border border-darwin-neutral-200 p-10 rounded-8 w-full"
                            placeholder="Ex- 2053, Anagha Enclave, 4th Floor, D2 Flat"
                            value={formInput.line1}
                            onChange={formInputChangeHandler}
                            required
                        />
                    </div>

                    <div className="space-y-7">
                        <p className="font-700">City</p>
                        <input
                            id="city"
                            className="border border-darwin-neutral-200 p-10 rounded-8 w-full"
                            placeholder="Ex- Bengalore"
                            value={formInput.city}
                            onChange={formInputChangeHandler}
                            required
                        />
                    </div>

                    <div className="space-y-7">
                        <p className="font-700">State</p>
                        <input
                            id="state"
                            className="border border-darwin-neutral-200 p-10 rounded-8 w-full"
                            placeholder="Ex- Karnataka"
                            value={formInput.state}
                            onChange={formInputChangeHandler}
                            required
                        />
                    </div>

                    <div className="space-y-7">
                        <p className="font-700">Pincode</p>
                        <input
                            id="pincode"
                            className="border border-darwin-neutral-200 p-10 rounded-8 w-full"
                            placeholder="560102"
                            value={formInput.pincode}
                            onChange={formInputChangeHandler}
                            type="number"
                            required
                        />
                    </div>
                </div>
            ) : (
                <div className="flex-1 space-y-16 text-14 p-14 overflow-auto hide-scrollbar mt-2 bg-background-secondary text-text-secondary leading-6">
                    {patientAddressArray?.map((i, index) => (
                        <div className="flex space-x-8 p-16 bg-white rounded-12 shadow-usecase-card-elevated">
                            <div className="w-full space-y-4">
                                <p className="text-16 font-700">{i.address.line1}</p>
                                <p className="">
                                    {[i.address.line2, i.address.city, i.address.state]
                                        .filter(Boolean)
                                        .join(', ')}
                                </p>
                                <p className="font-600">Pincode: {i.address.pincode}</p>
                            </div>

                            <svg
                                className="relative top-5 w-14 h-13 fill-darwin-neutral-900"
                                viewBox="0 0 14 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                onClick={() => editAddressHandler(index)}
                            >
                                <path d="M1.84375 8.96094C1.9375 8.63281 2.125 8.32812 2.35938 8.09375L9.48438 0.96875C10.0703 0.382812 11.0312 0.382812 11.6172 0.96875L12.5312 1.88281C12.6016 1.95312 12.6719 2.04688 12.7188 2.11719C13.1172 2.70312 13.0469 3.5 12.5312 4.01562L5.40625 11.1406C5.38281 11.1641 5.33594 11.1875 5.3125 11.2344C5.07812 11.4219 4.82031 11.5625 4.53906 11.6562L1.70312 12.4766C1.51562 12.5469 1.30469 12.5 1.16406 12.3359C1 12.1953 0.953125 11.9844 1 11.7969L1.84375 8.96094ZM2.38281 11.1172L4.21094 10.5781C4.25781 10.5547 4.32812 10.5312 4.375 10.5078L3.53125 10.3438C3.34375 10.2969 3.17969 10.1562 3.15625 9.96875L2.99219 9.125C2.96875 9.17188 2.94531 9.24219 2.92188 9.28906L2.38281 11.1172ZM5.21875 9.73438L9.97656 4.97656L8.5 3.52344L3.76562 8.28125L4 9.47656L5.21875 9.73438Z" />
                            </svg>
                        </div>
                    ))}
                </div>
            )}

            {!isLoading && (
                <div className="flex items-center justify-center w-full px-16 py-12 border-t border-light">
                    <button
                        className="px-64 py-12 primary-btn font-700 ripple"
                        onClick={() => {
                            isNewOrEditAddress ? setAddressHandler() : setIsNewOrEditAddress(true);
                        }}
                    >
                        {isEditing
                            ? 'Edit Address'
                            : isNewOrEditAddress
                            ? 'Add Address'
                            : 'Add New Address'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default AddAddress;
